<template>
  <div class="flex flex-col bg-white rounded-xl border border-[#d8d8e2]">
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 bg-[#f8f8fa] border-b border-[#d8d8e2] rounded-t-xl"
    >
      <span class="text-base font-bold text-[#16161d] text-left">Convite completo</span>

      <div class="flex items-center gap-2 flex-wrap">
        <span
          v-if="isAwaitingReview"
          class="flex items-center gap-1 rounded px-2 py-1 border text-sm whitespace-nowrap bg-[#fef9e7] border-[#f1c21b] text-[#16161d]"
          data-testid="package-awaiting-review-badge"
        >
          Aguardando avaliação
        </span>

        <HandoverExpirationBadge :expires-at="packageData.expiresAt" show-date />

        <span
          class="text-xs font-semibold text-[#16161d] bg-white border border-[#d8d8e2] rounded px-2 py-1 whitespace-nowrap"
          data-testid="package-stations-count-chip"
        >
          {{ packageData.stations.length }} Plantões no convite
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-5 px-6 py-5">
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-[#e8e8ed] rounded-lg divide-y sm:divide-y-0 divide-[#e8e8ed] sm:divide-x"
        data-testid="package-info-row"
      >
        <div
          v-for="info in infoItems"
          :key="info.label"
          class="flex flex-col gap-1 px-4 py-3 min-w-0"
        >
          <span class="text-xs text-left text-[#7f7f8e]">{{ info.label }}</span>
          <span class="text-sm font-semibold text-left text-[#16161d] truncate">
            {{ info.value }}
          </span>
        </div>
      </div>

      <StationsTable :stations="packageData.stations" :recurrence="packageData.recurrence" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { format, parseISO } from 'date-fns'
import { PackageResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import { PackageStatus } from '@/Network/Types/Requests/HandoverInviteRequests'
import { formatCurrency } from '@/utils/formatters'
import StationsTable from './StationsTable.vue'
import HandoverExpirationBadge from './HandoverExpirationBadge.vue'

const props = defineProps<{
  packageData: PackageResponse
}>()

const isAwaitingReview = computed(() => props.packageData.status === PackageStatus.IN_ADMIN_QUEUE)

// Um convite pode agrupar plantões de unidades diferentes — exibimos a primeira
// e sinalizamos as demais com um contador, como na listagem de histórico.
const healthUnitNames = computed(() => {
  const names = props.packageData.stations
    .map((station) => station.healthUnit?.name)
    .filter((name): name is string => !!name)
  return [...new Set(names)]
})

const healthUnitName = computed(() => {
  const [first, ...rest] = healthUnitNames.value
  if (!first) return '-'
  return rest.length > 0 ? `${first} +${rest.length}` : first
})

const formattedCreatedAt = computed(() => {
  try {
    return format(parseISO(props.packageData.createdAt), "dd/MM/yyyy 'às' HH:mm")
  } catch {
    return props.packageData.createdAt
  }
})

const formattedTotalValue = computed(() => {
  const total = props.packageData.stations.reduce((sum, s) => sum + (s.value ?? 0), 0)
  return formatCurrency(total)
})

const infoItems = computed(() => [
  { label: 'Unidade', value: healthUnitName.value },
  { label: 'Escala', value: props.packageData.schedule.name },
  { label: 'Criado em', value: formattedCreatedAt.value },
  { label: 'Valor estimado total', value: formattedTotalValue.value }
])
</script>
